import { useEffect, useRef, useState } from 'react';

/**
 * A tiny on-screen log of raw pointer events, for diagnosing input issues on devices we can't attach a
 * debugger to (iPad Pencil). Enable by adding `?ptrdebug` to the URL. Shows the most recent events with
 * their type / id / contact size / phase, so a "skipped" stroke reveals whether iOS delivered it as
 * `pen`, as `touch`, as `pointercancel`, or not at all.
 */
export default function PointerDebug() {
  const [lines, setLines] = useState<string[]>([]);
  const bufRef = useRef<string[]>([]);
  const nRef = useRef(0);
  useEffect(() => {
    const t0 = performance.now();
    const push = (line: string) => {
      nRef.current += 1;
      bufRef.current = [`${String(nRef.current).padStart(3, '0')} +${String(Math.round(performance.now() - t0)).padStart(5)}ms ${line}`, ...bufRef.current].slice(0, 22);
      setLines(bufRef.current);
    };
    const log = (phase: string) => (e: PointerEvent) => push(`${phase} ${e.pointerType.padEnd(5)} #${e.pointerId} w${Math.round(e.width)}`);
    const d = log('DOWN'); const u = log('UP  '); const c = log('CNCL');
    // Lifecycle messages dispatched from the controller (stroke start / commit / flush).
    const onMsg = (ev: Event) => push(`» ${(ev as CustomEvent<string>).detail}`);
    // Raw TOUCH + GESTURE events: if a "skipped" tap fires touchstart / gesturestart but NO pointerdown,
    // Safari's own gesture recogniser is swallowing it (not our code / not the render).
    const tl = (name: string) => (e: Event) => push(`· ${name} n=${(e as TouchEvent).touches?.length ?? '?'}`);
    const ts = tl('touchstart'); const te = tl('touchend'); const gs = tl('GESTURE-start'); const gc = tl('GESTURE-chg');
    // Wheel — trackpad pinch on macOS/Chrome arrives as wheel+ctrlKey; logging it shows whether an iPad
    // Magic-Keyboard trackpad pinch produces ANY web event (it usually produces none — Safari page-zooms).
    const wl = (e: WheelEvent) => push(`wheel ${e.ctrlKey ? 'CTRL' : '    '} dy=${Math.round(e.deltaY)}`);
    // Main-thread stall detector: a long rAF gap = the thread was blocked (input can be dropped then).
    let last = performance.now();
    let raf = 0;
    const beat = (now: number) => {
      const gap = now - last;
      if (gap > 120) push(`⚠ stall ${Math.round(gap)}ms`);
      last = now;
      raf = requestAnimationFrame(beat);
    };
    raf = requestAnimationFrame(beat);
    window.addEventListener('pointerdown', d, true);
    window.addEventListener('pointerup', u, true);
    window.addEventListener('pointercancel', c, true);
    window.addEventListener('touchstart', ts, true);
    window.addEventListener('touchend', te, true);
    window.addEventListener('gesturestart', gs, true);
    window.addEventListener('gesturechange', gc, true);
    window.addEventListener('wheel', wl, true);
    window.addEventListener('ptrlog', onMsg as EventListener);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('pointerdown', d, true);
      window.removeEventListener('pointerup', u, true);
      window.removeEventListener('pointercancel', c, true);
      window.removeEventListener('touchstart', ts, true);
      window.removeEventListener('touchend', te, true);
      window.removeEventListener('gesturestart', gs, true);
      window.removeEventListener('gesturechange', gc, true);
      window.removeEventListener('wheel', wl, true);
      window.removeEventListener('ptrlog', onMsg as EventListener);
    };
  }, []);
  return (
    <div
      className="pointer-events-none fixed right-2 top-16 z-[100] max-w-[280px] rounded-md px-2 py-1.5"
      style={{ fontFamily: 'ui-monospace, monospace', fontSize: 11, lineHeight: 1.45, color: '#7CFC7C', background: 'rgba(0,0,0,0.72)', whiteSpace: 'pre' }}
    >
      {lines.length ? lines.map((l, i) => <div key={i}>{l}</div>) : <div>pointer log — draw to see events</div>}
    </div>
  );
}
